import React from 'react';
import { PlaylistSortOrder } from '../../../hooks/usePlaylistDetail';
import { Button, Card, CardContent, Checkbox, Chip, Tooltip, Typography } from '../../ui';
import { PlaylistVideo } from '../../../types/playlist';
import { formatDurationClock } from '../../../utils';
import { isSelectable, statusLabel, PlaylistVideoDates, toDownloadFileProps } from './playlistVideoHelpers';
import DownloadFormatIndicator from '../../shared/DownloadFormatIndicator';
import WatchedChip from '../../shared/WatchedChip';
import { REMOVAL_TOOLTIP_CLASS } from '../../shared/RemovalDescription';
import { SHARED_STATUS_CHIP_SMALL_STYLE, SHARED_COMPACT_CHIP_OVERRIDES } from '../../shared/chipStyles';

const compactStatusChipStyle: React.CSSProperties = {
  ...SHARED_STATUS_CHIP_SMALL_STYLE,
  ...SHARED_COMPACT_CHIP_OVERRIDES,
};

interface PlaylistVideoCardProps {
  sortOrder?: PlaylistSortOrder;
  video: PlaylistVideo;
  onIgnore: (ytId: string) => void;
  onUnignore: (ytId: string) => void;
  onVideoClick: (video: PlaylistVideo) => void;
  pendingId?: string | null;
  isSelected: (ytId: string) => boolean;
  onToggle: (ytId: string) => void;
}

const PlaylistVideoCard: React.FC<PlaylistVideoCardProps> = ({
  sortOrder = 'asc',
  video,
  onIgnore,
  onUnignore,
  onVideoClick,
  pendingId,
  isSelected,
  onToggle,
}) => {
  const status = statusLabel(video);
  const selectable = isSelectable(video);
  const isPending = pendingId === video.youtube_id;

  return (
    <Card
      className="mb-3 flex items-stretch cursor-pointer"
      style={{ borderRadius: 'var(--radius-ui)' }}
      onClick={() => onVideoClick(video)}
    >
      <div
        className="relative shrink-0 overflow-hidden bg-[var(--media-placeholder-background)] rounded-[var(--radius-thumb)] self-center ml-2 w-24 h-[54px] min-[400px]:w-[120px] min-[400px]:h-[68px]"
      >
        {video.thumbnail && (
          <img
            src={video.thumbnail}
            alt=""
            className="block w-full h-full object-cover rounded-[var(--radius-thumb)]"
            loading="lazy"
          />
        )}
        {selectable && (
          <div
            className="absolute top-1 left-1 z-10 rounded bg-[var(--media-overlay-background)]"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isSelected(video.youtube_id)}
              onChange={() => onToggle(video.youtube_id)}
              aria-label={`Select ${video.title || video.youtube_id}`}
            />
          </div>
        )}
      </div>

      <CardContent className="flex-1 min-w-0 py-2 px-3">
        <Typography variant="body2" className="line-clamp-2 mb-1" title={video.title || video.youtube_id}>
          {video.title || video.youtube_id}
        </Typography>
        <Typography variant="caption" className="text-muted-foreground line-clamp-1 block">
          {video.channel_name || '-'}
        </Typography>
        <div className="mt-1">
          <PlaylistVideoDates video={video} sortOrder={sortOrder} />
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-muted-foreground">
          <span className="whitespace-nowrap">{formatDurationClock(video.duration) || '-'}</span>
          {video.downloaded ? (
            <>
              <DownloadFormatIndicator {...toDownloadFileProps(video)} orientation="horizontal" compact />
              {video.ignored && (
                <Chip label={status.label} color={status.color} size="small" style={compactStatusChipStyle} />
              )}
            </>
          ) : status.tooltip ? (
            <Tooltip title={status.tooltip} enterTouchDelay={0} className={REMOVAL_TOOLTIP_CLASS}>
              <Chip label={status.label} color={status.color} size="small" style={compactStatusChipStyle} />
            </Tooltip>
          ) : (
            <Chip label={status.label} color={status.color} size="small" style={compactStatusChipStyle} />
          )}
          <WatchedChip watchedBy={video.watched_by || []} compact />
        </div>
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          {video.ignored ? (
            <Tooltip title="Include this video in this playlist's downloads and server sync again">
              <Button
                size="sm"
                variant="outlined"
                className="h-7 px-2.5"
                onClick={() => onUnignore(video.youtube_id)}
                disabled={isPending}
              >
                Include
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                video.downloaded
                  ? 'Exclude from this playlist: removed from synced server playlists and M3U files; the file stays on disk'
                  : 'Exclude from this playlist: skipped when downloading this playlist'
              }
            >
              <Button
                size="sm"
                variant="outlined"
                className="h-7 px-2.5"
                onClick={() => onIgnore(video.youtube_id)}
                disabled={isPending}
              >
                Exclude
              </Button>
            </Tooltip>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PlaylistVideoCard;
